import { useEffect, useLayoutEffect, useRef } from 'react';
import type { ThemeSpec } from '../themes';
import type { Point, Tool } from '@blackboard/shared';
import type { StrokeIndex } from './spatialIndex';
import { renderBackground, renderWoodFrame } from './backgroundRender';
import { renderStroke, renderCommittedStroke } from './brushes';
import { viewportBBox, type Viewport } from './viewport';
import { getFresh } from './freshnessTracker';
import { strokePath, pathToPath2D } from './freehand';
import { usePresence } from '../state/presence';
import { useBoard, type BoardMutation } from '../state/board';
import type { Stroke } from '@blackboard/shared';

interface LivePaintItem {
  tool: Tool;
  color: string;
  sizePx: number;
  points: Point[];
}

export interface BrushGhost {
  x: number;
  y: number;
  visible: boolean;
  /** Screen-space radius in CSS pixels. */
  r: number;
}

const CURSOR_STALE_MS = 8000;

interface Props {
  theme: ThemeSpec;
  viewport: Viewport;
  index: StrokeIndex;
  /** Live in-flight strokes; mutated outside React, read by the RAF loop. */
  liveRef: { current: Map<string, LivePaintItem> };
  /** Brush-size ghost cursor; mutated by pointer handlers, read by the RAF loop. */
  ghostRef: { current: BrushGhost };
  selfUserId?: string | null;
  highlightUserId?: string | null;
  onPointerDown: (e: React.PointerEvent) => void;
  onPointerMove: (e: React.PointerEvent) => void;
  onPointerUp: (e: React.PointerEvent) => void;
  onPointerLeave?: (e: React.PointerEvent) => void;
  onWheel: (e: React.WheelEvent) => void;
}

/** Screen transform: cssX = wx * zoom + offX (offX = W/2 - cx*zoom). */
interface ScreenT { zoom: number; offX: number; offY: number; W: number; H: number }

function screenTransform(v: Viewport): ScreenT {
  return {
    zoom: v.zoom,
    offX: v.screenW / 2 - v.cx * v.zoom,
    offY: v.screenH / 2 - v.cy * v.zoom,
    W: v.screenW,
    H: v.screenH,
  };
}

function sizePxMap(sizeIdx: number, _tool: Tool, theme: ThemeSpec): number {
  const [min, , max] = theme.sizes;
  return min + (max - min) * (sizeIdx - 1) / 9;
}

/**
 * 渲染架构（性能关键路径）：
 *  - 静态层是一块**持久画布**：只在内容变化（增量 mutation）或视口变换时
 *    重画，平移走自拷贝 + 边缘补绘，缩放先哑缩放占位再空闲时清晰重绘。
 *    任何人落笔/擦除都只重画受影响的 bbox，不再整屏重算。
 *  - live 层每帧全清重画：只有自己当前笔画 + 少量淡入 + 幽灵光标，
 *    数量恒定小，成本可忽略。
 *  - 单 RAF 循环直读 refs/store，绘制过程零 React 渲染。
 */
export function CanvasStage(p: Props) {
  const bgRef = useRef<HTMLCanvasElement>(null);
  const staticRef = useRef<HTMLCanvasElement>(null);
  const liveRef = useRef<HTMLCanvasElement>(null);
  const tmpRef = useRef<HTMLCanvasElement | null>(null);
  const dpr = typeof window !== 'undefined' ? window.devicePixelRatio || 1 : 1;

  // Latest-value refs — the RAF loop must survive prop updates without restart.
  const vpRef = useRef(p.viewport);
  vpRef.current = p.viewport;
  const themeRef = useRef(p.theme);
  themeRef.current = p.theme;
  const hlRef = useRef(p.highlightUserId);
  hlRef.current = p.highlightUserId;
  const needFullRef = useRef(true);
  const crispScheduledRef = useRef(false);
  const lastTRef = useRef<ScreenT | null>(null);
  const lastHlRef = useRef<string | null | undefined>(undefined);

  // Resize canvases — runs before paint effects on each (re)mount.
  useLayoutEffect(() => {
    const w = p.viewport.screenW;
    const h = p.viewport.screenH;
    for (const r of [bgRef, staticRef, liveRef]) {
      const c = r.current;
      if (!c) continue;
      if (c.width !== Math.floor(w * dpr) || c.height !== Math.floor(h * dpr)) {
        c.width = Math.floor(w * dpr);
        c.height = Math.floor(h * dpr);
        c.style.width = `${w}px`;
        c.style.height = `${h}px`;
      }
    }
    // Any resize wipes the static canvas content.
    needFullRef.current = true;
  }, [p.viewport.screenW, p.viewport.screenH, dpr]);

  // Background
  useEffect(() => {
    const c = bgRef.current;
    if (!c) return;
    const ctx = c.getContext('2d')!;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    renderBackground(ctx, p.theme, p.viewport.screenW, p.viewport.screenH);
  }, [p.theme, p.viewport.screenW, p.viewport.screenH, dpr]);

  // Theme/dpr change invalidates rasterized strokes too.
  useEffect(() => {
    needFullRef.current = true;
    lastTRef.current = null;
  }, [p.theme, dpr]);

  // ---- the render loop (static incremental + live) ----
  useEffect(() => {
    let raf = 0;
    let lastPrune = 0;

    const tick = () => {
      raf = requestAnimationFrame(tick);
      const st = staticRef.current;
      const lv = liveRef.current;
      if (!st || !lv) return;
      const ctxS = st.getContext('2d')!;
      const ctxL = lv.getContext('2d', { desynchronized: true })!;
      const theme = themeRef.current;
      const vp = vpRef.current;
      const T = screenTransform(vp);

      renderStatic(ctxS, st, T, theme);
      renderLive(ctxL, lv, T, theme);
      // 木框是前景：盖住放大后延伸出屏的笔画，黑板框永远完整
      if (theme.bgPattern === 'wood-frame') {
        renderWoodFrame(ctxL, theme, p.viewport.screenW, p.viewport.screenH);
      }
    };

    // ---------------- static layer ----------------
    function applyWorld(ctx: CanvasRenderingContext2D, T: ScreenT, d: number) {
      ctx.setTransform(d * T.zoom, 0, 0, d * T.zoom, d * T.offX, d * T.offY);
    }

    function drawStrokeWorld(ctx: CanvasRenderingContext2D, s: Stroke, theme: ThemeSpec) {
      renderCommittedStroke(ctx, s, (idx, tool) => sizePxMap(idx, tool, theme));
      const hl = hlRef.current;
      if (hl != null && s.user_id === hl) {
        const px = sizePxMap(s.size, s.tool, theme);
        ctx.save();
        ctx.globalAlpha = 0.5;
        ctx.strokeStyle = '#a78bfa';
        ctx.lineWidth = px + 4;
        ctx.lineJoin = 'round';
        ctx.lineCap = 'round';
        const outline = strokePath(s.points, { size: px, simulatePressure: s.points.every((pt) => pt.pressure === 0.5) });
        if (outline.length >= 3) ctx.stroke(pathToPath2D(outline));
        ctx.restore();
      }
    }

    function fullRepaint(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, T: ScreenT, theme: ThemeSpec) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const vp = vpRef.current;
      const bb = viewportBBox(vp);
      const margin = (bb.x2 - bb.x1) * 0.25;
      const strokes = useBoard.getState().index.search({ x1: bb.x1 - margin, y1: bb.y1 - margin, x2: bb.x2 + margin, y2: bb.y2 + margin });
      applyWorld(ctx, T, dpr);
      for (const s of strokes) drawStrokeWorld(ctx, s, theme);
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }

    function repaintWorldRect(ctx: CanvasRenderingContext2D, T: ScreenT, theme: ThemeSpec, world: { x1: number; y1: number; x2: number; y2: number }) {
      // Clear + re-render only strokes intersecting `world`, clipped so nothing
      // outside the rect is double-composited.
      const pad = (16 * T.zoom) + 16;
      const sx = world.x1 * T.zoom + T.offX - pad;
      const sy = world.y1 * T.zoom + T.offY - pad;
      const sw = (world.x2 - world.x1) * T.zoom + pad * 2;
      const sh = (world.y2 - world.y1) * T.zoom + pad * 2;
      const dx = sx * dpr, dy = sy * dpr, dw = sw * dpr, dh = sh * dpr;
      if (dw <= 0 || dh <= 0) return;
      ctx.save();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(dx, dy, dw, dh);
      ctx.beginPath();
      ctx.rect(dx, dy, dw, dh);
      ctx.clip();
      const margin = (world.x2 - world.x1) * 0.5 + 40 / T.zoom;
      const strokes = useBoard.getState().index.search({ x1: world.x1 - margin, y1: world.y1 - margin, x2: world.x2 + margin, y2: world.y2 + margin });
      applyWorld(ctx, T, dpr);
      for (const s of strokes) drawStrokeWorld(ctx, s, theme);
      ctx.restore();
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }

    function scheduleCrispRepaint() {
      if (crispScheduledRef.current) return;
      crispScheduledRef.current = true;
      const run = () => {
        crispScheduledRef.current = false;
        needFullRef.current = true;
      };
      if (typeof requestIdleCallback === 'function') {
        requestIdleCallback(() => run(), { timeout: 200 });
      } else {
        setTimeout(run, 120);
      }
    }

    // ---- pan 暴露条带的延迟补绘 ----
    // 平移帧只做 self-copy（帧成本恒定）；新暴露的边缘区域记入 pendingStrips，
    // 等变换静止的下一帧再补绘。拖动中暴露区显示背景层（永远正确），
    // 这让大幅甩动不再逐帧执行全高度 strip 重绘。
    type WRect = { x1: number; y1: number; x2: number; y2: number };
    let pendingStrips: WRect[] = [];
    let pannedThisFrame = false;

    function pushPendingStrip(r: WRect, eps: number) {
      // 相邻/相交条带合并：同向连续拖动收敛为一条，避免列表膨胀
      for (;;) {
        const i = pendingStrips.findIndex((p) =>
          p.x1 - eps <= r.x2 && r.x1 <= p.x2 + eps &&
          p.y1 - eps <= r.y2 && r.y1 <= p.y2 + eps);
        if (i < 0) break;
        const p = pendingStrips.splice(i, 1)[0]!;
        r = {
          x1: Math.min(p.x1, r.x1), y1: Math.min(p.y1, r.y1),
          x2: Math.max(p.x2, r.x2), y2: Math.max(p.y2, r.y2),
        };
      }
      pendingStrips.push(r);
    }

    function tmpCanvasFor(src: HTMLCanvasElement): HTMLCanvasElement {
      let t = tmpRef.current;
      if (!t) { t = document.createElement('canvas'); tmpRef.current = t; }
      if (t.width !== src.width || t.height !== src.height) { t.width = src.width; t.height = src.height; }
      const tc = t.getContext('2d')!;
      tc.setTransform(1, 0, 0, 1, 0, 0);
      tc.clearRect(0, 0, t.width, t.height);
      tc.drawImage(src, 0, 0);
      return t;
    }

    function renderStatic(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, T: ScreenT, theme: ThemeSpec) {
      const hl = hlRef.current ?? null;
      if (lastHlRef.current !== undefined && lastHlRef.current !== hl) needFullRef.current = true;
      lastHlRef.current = hl;

      const last = lastTRef.current;
      pannedThisFrame = false;

      // 1) viewport transform change
      if (last && last.W === T.W && last.H === T.H) {
        if (last.zoom === T.zoom && (last.offX !== T.offX || last.offY !== T.offY)) {
          // pure pan: self-copy now, repaint exposed strips when the transform settles
          const dx = T.offX - last.offX;
          const dy = T.offY - last.offY;
          const tmp = tmpCanvasFor(canvas);
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.drawImage(tmp, dx * dpr, dy * dpr);
          const vp = vpRef.current;
          const bb = viewportBBox(vp);
          const m = 60 / T.zoom;
          const eps = 2 / T.zoom;
          if (dx > 0.5) pushPendingStrip({ x1: bb.x1 - m, y1: bb.y1 - m, x2: bb.x1 + dx / T.zoom, y2: bb.y2 + m }, eps);
          if (dx < -0.5) pushPendingStrip({ x1: bb.x2 + dx / T.zoom, y1: bb.y1 - m, x2: bb.x2 + m, y2: bb.y2 + m }, eps);
          if (dy > 0.5) pushPendingStrip({ x1: bb.x1 - m, y1: bb.y1 - m, x2: bb.x2 + m, y2: bb.y1 + dy / T.zoom }, eps);
          if (dy < -0.5) pushPendingStrip({ x1: bb.x1 - m, y1: bb.y2 + dy / T.zoom, x2: bb.x2 + m, y2: bb.y2 + m }, eps);
          pannedThisFrame = true;
        } else if (last.zoom !== T.zoom) {
          // zoom: scaled blit for instant feedback, crisp repaint when idle
          pendingStrips = [];
          const tmp = tmpCanvasFor(canvas);
          const s = T.zoom / last.zoom;
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          ctx.translate(T.offX * dpr, T.offY * dpr);
          ctx.scale(s, s);
          ctx.translate(-last.offX * dpr, -last.offY * dpr);
          ctx.drawImage(tmp, 0, 0);
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          scheduleCrispRepaint();
        }
      }

      // 2) content mutations (only when transform is settled — full flag wins)
      if (!needFullRef.current) {
        const muts = useBoard.getState().mutations;
        if (muts.length > 0) {
          const pending: BoardMutation[] = muts.splice(0, muts.length);
          const adds: Stroke[] = [];
          let removed = false;
          for (const m of pending) {
            if (m.kind === 'add') adds.push(m.stroke);
            else if (m.kind === 'adds') adds.push(...m.strokes);
            else if (m.kind === 'remove') { repaintWorldRect(ctx, T, theme, m.bbox); removed = true; }
            else { needFullRef.current = true; break; }
          }
          if (adds.length > 0 && !needFullRef.current) {
            ctx.save();
            applyWorld(ctx, T, dpr);
            for (const s of adds) drawStrokeWorld(ctx, s, theme);
            ctx.restore();
            ctx.setTransform(1, 0, 0, 1, 0, 0);
          }
          void removed;
        }
      }

      // 3) full repaint when flagged
      if (needFullRef.current) {
        needFullRef.current = false;
        pendingStrips = [];
        fullRepaint(ctx, canvas, T, theme);
      } else if (pendingStrips.length > 0 && !pannedThisFrame) {
        // transform settled → fill in everything panning exposed
        const strips = pendingStrips.splice(0);
        for (const r of strips) repaintWorldRect(ctx, T, theme, r);
      }

      lastTRef.current = T;
    }

    // ---------------- live layer ----------------
    function renderLive(ctx: CanvasRenderingContext2D, canvas: HTMLCanvasElement, T: ScreenT, theme: ThemeSpec) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

      // Freshness fade for foreign strokes committed recently — plain alpha,
      // no shadowBlur (that was the most expensive op in the old loop).
      if (p.selfUserId != null) {
        const freshEntries = getFresh();
        if (freshEntries.length > 0) {
          ctx.save();
          ctx.translate(T.offX, T.offY);
          ctx.scale(T.zoom, T.zoom);
          for (const { strokeId, fade } of freshEntries) {
            const s = useBoard.getState().index.getById(strokeId);
            if (!s || s.user_id === p.selfUserId) continue;
            const px = sizePxMap(s.size, s.tool, theme);
            ctx.save();
            ctx.globalAlpha = 0.4 * fade;
            renderStroke(ctx, { tool: s.tool, color: s.color, size: px, points: s.points });
            ctx.restore();
          }
          ctx.restore();
        }
      }

      // In-progress strokes — world space (own current stroke + legacy remote streams)
      ctx.save();
      ctx.translate(T.offX, T.offY);
      ctx.scale(T.zoom, T.zoom);
      for (const s of p.liveRef.current.values()) {
        renderStroke(ctx, { tool: s.tool, color: s.color, size: s.sizePx, points: s.points });
      }
      ctx.restore();

      // Remote cursors (legacy senders only in v2) — screen space
      const now = Date.now();
      if (now - lastPrune > 2000) {
        lastPrune = now;
        usePresence.getState().pruneStale(CURSOR_STALE_MS);
      }
      for (const c2 of usePresence.getState().cursors.values()) {
        if (c2.user_id === p.selfUserId) continue;
        if (now - c2.ts > CURSOR_STALE_MS) continue;
        const sx = c2.x * T.zoom + T.offX;
        const sy = c2.y * T.zoom + T.offY;
        ctx.save();
        ctx.fillStyle = c2.color;
        ctx.beginPath();
        ctx.arc(sx, sy, 5, 0, Math.PI * 2);
        ctx.fill();
        ctx.strokeStyle = 'white';
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.font = '12px ui-sans-serif, system-ui, sans-serif';
        ctx.textBaseline = 'top';
        const label = c2.name;
        const pad = 4;
        const w = ctx.measureText(label).width + pad * 2;
        ctx.fillStyle = c2.color;
        ctx.fillRect(sx + 8, sy - 4, w, 18);
        ctx.fillStyle = 'white';
        ctx.fillText(label, sx + 8 + pad, sy - 2);
        ctx.restore();
      }

      // Brush ghost — screen space ring showing the exact brush/eraser footprint
      const g = p.ghostRef.current;
      if (g.visible && g.r > 0) {
        ctx.save();
        ctx.beginPath();
        ctx.arc(g.x, g.y, g.r, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(255,255,255,0.9)';
        ctx.lineWidth = 1.25;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(g.x, g.y, g.r + 1.25, 0, Math.PI * 2);
        ctx.strokeStyle = 'rgba(0,0,0,0.35)';
        ctx.lineWidth = 0.75;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(g.x, g.y, 1, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(255,255,255,0.9)';
        ctx.fill();
        ctx.restore();
      }
    }

    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [p.index, p.theme, p.liveRef, p.ghostRef, p.selfUserId, dpr]);

  return (
    <div
      className="absolute inset-0 touch-none select-none"
      onPointerDown={p.onPointerDown}
      onPointerMove={p.onPointerMove}
      onPointerUp={p.onPointerUp}
      onPointerCancel={p.onPointerUp}
      onPointerLeave={p.onPointerLeave}
      onWheel={p.onWheel}
      onContextMenu={(e) => e.preventDefault()}
    >
      <canvas ref={bgRef} id="canvas-bg" className="absolute inset-0 pointer-events-none" />
      <canvas ref={staticRef} id="canvas-static" className="absolute inset-0 pointer-events-none" />
      <canvas ref={liveRef} id="canvas-live" className="absolute inset-0 pointer-events-none" />
    </div>
  );
}
